import type {
  GetMeetingResponse,
  JoinMeetingRequest,
  JoinMeetingResponse,
} from '@saywell/shared';

const API_BASE = import.meta.env.VITE_API_ENDPOINT || '';

/**
 * Get meeting info by code (for validation)
 * @param code - The meeting code
 * @param token - Optional provider token for authentication
 */
export async function getMeeting(code: string, token?: string): Promise<GetMeetingResponse> {
  const normalizedCode = code.toUpperCase().trim();
  const url = new URL(`${API_BASE}/meetings/${encodeURIComponent(normalizedCode)}`);
  if (token) {
    url.searchParams.set('token', token);
  }
  const response = await fetch(url.toString());

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to connect' }));
    return { success: false, error: error.error || 'Meeting not found' };
  }

  return response.json();
}

/**
 * Join a meeting as a patient
 */
export async function joinMeeting(
  code: string,
  participantName: string
): Promise<JoinMeetingResponse> {
  const normalizedCode = code.toUpperCase().trim();
  const request: JoinMeetingRequest = {
    participantType: 'patient',
    participantName,
  };

  const response = await fetch(
    `${API_BASE}/meetings/${encodeURIComponent(normalizedCode)}/join`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to join meeting' }));
    return { success: false, error: error.error || 'Failed to join meeting' };
  }

  return response.json();
}

/**
 * Join a meeting as a provider (agent)
 */
export async function joinMeetingAsProvider(
  code: string,
  providerName: string,
  agentId: string
): Promise<JoinMeetingResponse> {
  const normalizedCode = code.toUpperCase().trim();
  const request: JoinMeetingRequest = {
    participantType: 'agent',
    participantName: providerName,
    agentId,
  };

  const response = await fetch(
    `${API_BASE}/meetings/${encodeURIComponent(normalizedCode)}/join`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to join meeting' }));
    return { success: false, error: error.error || 'Failed to join meeting' };
  }

  return response.json();
}

/**
 * Format expiration time for display
 */
export function formatTimeRemaining(expiresAt: number): string {
  const now = Date.now();
  const remaining = expiresAt - now;

  if (remaining <= 0) {
    return 'Expired';
  }

  const minutes = Math.floor(remaining / 60000);
  const seconds = Math.floor((remaining % 60000) / 1000);

  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
}
